import React, { useState, useEffect } from 'react';
import { wholesaleCustomers } from '../../services/api';
import { Save, Building2, User } from 'lucide-react';

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

const formatPaymentType = (type) => {
  switch (type) {
    case 'invoice':     return 'Invoice/Net Terms';
    case 'credit_card': return 'Credit Card';
    case 'both':        return 'Invoice or Credit Card';
    default:            return type || '—';
  }
};

const formatCurrency = (n) => '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CustomerProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    phone: '',
    addressLine: '',
    city: '',
    state: '',
    zip: '',
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    wholesaleCustomers.getProfile()
      .then((res) => {
        const data = res.data.data;
        setProfile(data);
        setForm({
          phone:       data.phone || '',
          addressLine: data.addressLine || '',
          city:        data.city || '',
          state:       data.state || '',
          zip:         data.zip || '',
        });
      })
      .catch(() => setError('Failed to load profile. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await wholesaleCustomers.updateProfile(form);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      setSaveError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">{error}</div>
      </div>
    );
  }

  const { companyName, abcLicenseNumber, resaleNumber, paymentType, netDays, creditCap, rep } = profile;
  const showNetDays  = (paymentType === 'invoice' || paymentType === 'both') && netDays;
  const showCredit   = parseFloat(creditCap) > 0;

  return (
    <div className="container py-4">
      <h4 className="fw-bold mb-4">My Profile</h4>

      <div className="row g-4">

        {/* Left column — editable contact & shipping */}
        <div className="col-12 col-md-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom d-flex align-items-center gap-2 py-3">
              <User size={18} className="text-primary" />
              <span className="fw-semibold">Contact &amp; Shipping</span>
            </div>
            <div className="card-body p-4">

              {saveSuccess && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  Profile updated successfully.
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSaveSuccess(false)}
                    aria-label="Close"
                  />
                </div>
              )}

              {saveError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {saveError}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSaveError(null)}
                    aria-label="Close"
                  />
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="phone" className="form-label fw-semibold">Phone</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    className="form-control"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="addressLine" className="form-label fw-semibold">Street Address</label>
                  <input
                    id="addressLine"
                    name="addressLine"
                    type="text"
                    className="form-control"
                    value={form.addressLine}
                    onChange={handleChange}
                  />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-12 col-sm-5">
                    <label htmlFor="city" className="form-label fw-semibold">City</label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      className="form-control"
                      value={form.city}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-12 col-sm-4">
                    <label htmlFor="state" className="form-label fw-semibold">State</label>
                    <select
                      id="state"
                      name="state"
                      className="form-select"
                      value={form.state}
                      onChange={handleChange}
                    >
                      <option value="">— Select —</option>
                      {US_STATES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-sm-3">
                    <label htmlFor="zip" className="form-label fw-semibold">Zip</label>
                    <input
                      id="zip"
                      name="zip"
                      type="text"
                      className="form-control"
                      value={form.zip}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary d-inline-flex align-items-center gap-2"
                    disabled={saving}
                  >
                    {saving
                      ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Saving...</>
                      : <><Save size={16} /> Save Changes</>
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right column — read-only info + rep */}
        <div className="col-12 col-md-5 d-flex flex-column gap-4">

          {/* Account Info card */}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom d-flex align-items-center gap-2 py-3">
              <Building2 size={18} className="text-primary" />
              <span className="fw-semibold">Account Info</span>
            </div>
            <div className="card-body p-4">
              <dl className="row mb-0 g-2">
                <dt className="col-5 text-muted fw-normal small">Company</dt>
                <dd className="col-7 fw-semibold mb-0">{companyName || '—'}</dd>

                {abcLicenseNumber && (
                  <>
                    <dt className="col-5 text-muted fw-normal small">ABC License</dt>
                    <dd className="col-7 mb-0">{abcLicenseNumber}</dd>
                  </>
                )}

                {resaleNumber && (
                  <>
                    <dt className="col-5 text-muted fw-normal small">Resale #</dt>
                    <dd className="col-7 mb-0">{resaleNumber}</dd>
                  </>
                )}

                <dt className="col-5 text-muted fw-normal small">Payment</dt>
                <dd className="col-7 mb-0">{formatPaymentType(paymentType)}</dd>

                {showNetDays && (
                  <>
                    <dt className="col-5 text-muted fw-normal small">Terms</dt>
                    <dd className="col-7 mb-0">Net {netDays} days</dd>
                  </>
                )}

                {showCredit && (
                  <>
                    <dt className="col-5 text-muted fw-normal small">Credit Limit</dt>
                    <dd className="col-7 mb-0">{formatCurrency(creditCap)}</dd>
                  </>
                )}
              </dl>
            </div>
          </div>

          {/* Assigned Rep card */}
          {rep !== null && rep && (
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom d-flex align-items-center gap-2 py-3">
                <User size={18} className="text-primary" />
                <span className="fw-semibold">Your Account Rep</span>
              </div>
              <div className="card-body p-4">
                <div className="fw-semibold mb-1">{rep.firstName} {rep.lastName}</div>
                <a href={`mailto:${rep.email}`} className="text-decoration-none text-primary small">{rep.email}</a>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
